// Shared address-input component — ported from
// prooveai-portal-v2/frontend/src/components/telehealthShared.js
// (EMPTY_ADDRESS/StreetAutocomplete/AddressFields), used by Report_
// Generation.js here and by labariumaiportal's Register.js, so both
// apps' address objects stay structurally identical (both eventually
// hash through the same compute_address_hash shape server-side).
import React from "react";
import axios from "axios";
import {
  Box, Typography, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Button, Autocomplete,
} from "@mui/material";
import { CheckCircleOutline, WarningAmber } from "@mui/icons-material";

export const EMPTY_ADDRESS = { line1: "", city: "", state: "", zip: "", unit: "" };

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA",
  "ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK",
  "OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC",
];

// 2026-10-15 fix (proper mandatory validation + tailored input fields) —
// best-practice US zip validation, 5 digits with an optional "+4" extension.
export function isValidZip(zip) {
  return /^\d{5}(-\d{4})?$/.test((zip || "").trim());
}

const BRAND = { navy: "#00357a", gold: "#c9a96e" };

function formatAddressForDisplay({ street, secondary, city, state, zip }) {
  return `${street}${secondary ? `, ${secondary}` : ""}, ${city}, ${state} ${zip}`;
}

// Labarium identity plan — SECOND, independent safety net against address
// typos/edits the frontend Smarty autocomplete (StreetAutocomplete, above)
// alone can't catch — that only helps someone who actually picks a
// suggestion and never edits it afterward. Right before a form actually
// submits, this calls the backend's OWN authoritative Smarty validation
// (POST /api/validate-address — same validate_address_with_smarty helper
// compute_address_hash itself calls, preview-only, writes nothing) and
// forces an explicit "is this correct?" confirmation before the caller's
// real submit proceeds. Every address-entry call site in this app
// (Report_Generation.js) and labariumaiportal's Register.js/OrderTest.js/
// MyPatients.js uses this exact same hook/dialog, via each app's own copy
// of this file — one confirm UX everywhere, not several.
//
// Usage: const { confirmAddress, dialog } = useAddressConfirm(API_BASE);
// render {dialog} once anywhere in the tree, and right before an existing
// submit handler does its real work, do:
//   const confirmed = await confirmAddress(address);
//   if (!confirmed) return; // user chose "No, let me edit" — don't submit
//   // use `confirmed` (not the original `address`) as the address to send —
//   // it's auto-corrected to Smarty's standardized values when validated,
//   // and left as-typed when Smarty couldn't confirm it.
export function useAddressConfirm(apiBase) {
  const [pending, setPending] = React.useState(null); // { original, validated, standardized, resolve }
  const [checking, setChecking] = React.useState(false);
  // Guards against a double-click firing two overlapping confirmAddress
  // calls — the second setPending would silently overwrite the first and
  // leave its caller's promise unresolved forever. A ref (not state) since
  // this has to be checked synchronously, before any re-render.
  const inFlightRef = React.useRef(false);

  const confirmAddress = React.useCallback(async (address) => {
    if (inFlightRef.current) return null;
    inFlightRef.current = true;
    setChecking(true);
    let validated = false;
    let standardized = null;
    try {
      const res = await axios.post(`${apiBase}/api/validate-address`, {
        street: address.line1, unit: address.unit || "", city: address.city, state: address.state, zip: address.zip,
      });
      validated = !!res.data?.validated;
      standardized = res.data?.standardized || null;
    } catch {
      // Network/server failure — never silently skip the checkpoint; fall
      // through to the "couldn't verify" variant of the SAME dialog so the
      // person still gets a forced confirmation either way.
      validated = false;
      standardized = null;
    } finally {
      setChecking(false);
    }
    try {
      return await new Promise((resolve) => setPending({ original: address, validated, standardized, resolve }));
    } finally {
      inFlightRef.current = false;
    }
  }, [apiBase]);

  const handleConfirm = () => {
    const { original, validated, standardized, resolve } = pending;
    const finalAddress = validated && standardized
      ? { ...original, line1: standardized.street, unit: standardized.secondary || "", city: standardized.city, state: standardized.state, zip: standardized.zip }
      : original;
    setPending(null);
    resolve(finalAddress);
  };

  const handleEdit = () => {
    const { resolve } = pending;
    setPending(null);
    resolve(null);
  };

  const displayAddress = pending && (pending.validated && pending.standardized
    ? formatAddressForDisplay(pending.standardized)
    : formatAddressForDisplay({ street: pending.original.line1, secondary: pending.original.unit, city: pending.original.city, state: pending.original.state, zip: pending.original.zip }));

  const dialog = (
    <Dialog open={!!pending} onClose={handleEdit} maxWidth="xs" fullWidth
      slotProps={{ paper: { sx: { borderRadius: 3 } } }}>
      {pending && (
        <>
          <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 800, color: BRAND.navy, fontSize: 17 }}>
            {pending.validated
              ? <CheckCircleOutline sx={{ color: "#22c55e" }} />
              : <WarningAmber sx={{ color: BRAND.gold }} />}
            {pending.validated ? "Confirm this address" : "We couldn't verify this address"}
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ fontSize: 14, color: "#475569", mb: 1.5 }}>
              {pending.validated
                ? "We validated this address as:"
                : "It may still be correct (e.g. a new address not yet in postal records), but please double-check it:"}
            </Typography>
            <Typography sx={{
              fontSize: 15, fontWeight: 700, color: "#1e293b", p: 1.5, borderRadius: 2,
              bgcolor: pending.validated ? "rgba(0,53,122,0.05)" : "rgba(201,169,110,0.15)",
              border: pending.validated ? "none" : "1px solid rgba(201,169,110,0.4)",
            }}>
              {displayAddress}
            </Typography>
            <Typography sx={{ fontSize: 13, color: "#64748b", mt: 1.5 }}>Is this correct?</Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button onClick={handleEdit} sx={{ textTransform: "none", color: "#64748b", fontWeight: 600 }}>
              No, let me edit
            </Button>
            <Button onClick={handleConfirm} variant="contained"
              sx={{ textTransform: "none", fontWeight: 700, bgcolor: BRAND.navy, "&:hover": { bgcolor: "#001f4d" } }}>
              Yes, this is correct
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );

  return { confirmAddress, dialog, checking };
}

// NPPES NPI verification (2026-10-16, CEO-approved research plan) —
// labariumaireport's own copy of labariumaiportal's useNpiConfirm (same
// cross-app duplication precedent useAddressConfirm above already set:
// one confirm UX everywhere, each app keeps its own copy since these are
// two separate bundles). Differs from the portal's version only in
// hitting THIS app's own proxy route (POST /api/admin/portal-users/
// verify-npi, which itself proxies into the portal's x-internal-api-key-
// gated /internal/verify-npi) instead of the portal's public endpoint
// directly — the portal's CORS allowlist is locked to its own
// FRONTEND_URL, so a browser call from this app's admin UI can't reach
// it cross-origin at all. `extraHeaders` lets the caller pass its own
// authHeaders() (admin Bearer token), same convention
// rcm_create_portal_clinician's own caller already uses for the sibling
// clinician-creation call.
export function useNpiConfirm(apiBase, extraHeaders) {
  const [pending, setPending] = React.useState(null); // { outcome, registryName, resolve }
  const [checking, setChecking] = React.useState(false);
  const inFlightRef = React.useRef(false);

  const confirmNpi = React.useCallback(async (npi, firstName, lastName) => {
    const npiDigits = (npi || "").replace(/\D/g, "");
    if (!npiDigits) return false; // nothing to verify — never show a dialog for a blank NPI
    if (inFlightRef.current) return false;
    inFlightRef.current = true;
    setChecking(true);
    let outcome = "unavailable";
    let registryName = null;
    try {
      const res = await axios.post(`${apiBase}/api/admin/portal-users/verify-npi`, {
        npi: npiDigits, first_name: firstName, last_name: lastName,
      }, { headers: extraHeaders ? extraHeaders() : undefined });
      outcome = res.data?.outcome || "unavailable";
      registryName = res.data?.registry_name || null;
    } catch {
      outcome = "unavailable";
      registryName = null;
    } finally {
      setChecking(false);
    }
    try {
      return await new Promise((resolve) => setPending({ outcome, registryName, resolve }));
    } finally {
      inFlightRef.current = false;
    }
  }, [apiBase, extraHeaders]);

  const handleContinue = () => {
    const { resolve } = pending;
    setPending(null);
    resolve(true);
  };

  const handleEdit = () => {
    const { resolve } = pending;
    setPending(null);
    resolve(false);
  };

  const matched = pending?.outcome === "matched";

  const dialog = (
    <Dialog open={!!pending} onClose={handleEdit} maxWidth="xs" fullWidth
      slotProps={{ paper: { sx: { borderRadius: 3 } } }}>
      {pending && (
        <>
          <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 800, color: BRAND.navy, fontSize: 17 }}>
            {matched
              ? <CheckCircleOutline sx={{ color: "#22c55e" }} />
              : <WarningAmber sx={{ color: BRAND.gold }} />}
            {matched ? "Confirm this provider" : "We couldn't verify this provider"}
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ fontSize: 14, color: "#475569", mb: 1.5 }}>
              {matched
                ? "We verified this provider in the NPPES registry as:"
                : "It may still be correct (e.g. the registry may be out of date), but please double-check it:"}
            </Typography>
            {matched && (
              <Typography sx={{
                fontSize: 15, fontWeight: 700, color: "#1e293b", p: 1.5, borderRadius: 2,
                bgcolor: "rgba(0,53,122,0.05)",
              }}>
                Dr. {pending.registryName}
              </Typography>
            )}
            <Typography sx={{ fontSize: 13, color: "#64748b", mt: 1.5 }}>
              {matched ? "Is this you?" : "This never blocks account creation — you can continue either way."}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button onClick={handleEdit} sx={{ textTransform: "none", color: "#64748b", fontWeight: 600 }}>
              {matched ? "No, let me edit" : "Let me edit"}
            </Button>
            <Button onClick={handleContinue} variant="contained"
              sx={{ textTransform: "none", fontWeight: 700, bgcolor: BRAND.navy, "&:hover": { bgcolor: "#001f4d" } }}>
              {matched ? "Yes, this is correct" : "Continue anyway"}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );

  return { confirmNpi, dialog, checking };
}

// Smarty US Autocomplete Pro — suggestions as the person types. The browser
// key comes from REACT_APP_SMARTY_WEBSITE_KEY; when it's empty the street
// field is a plain text input and nothing else changes.
const SMARTY_WEBSITE_KEY = process.env.REACT_APP_SMARTY_WEBSITE_KEY || "";

function StreetAutocomplete({ isDark, inputSx, value, onChange, error }) {
  const [suggestions, setSuggestions] = React.useState([]);
  // 2026-10-08 — when a street address has more than one known unit,
  // Smarty's first-pass `secondary` field is just a generic placeholder
  // (e.g. the literal word "APT"), not a real unit number — it means
  // "ambiguous, ask again." `unitChoices` holds the real per-unit list
  // from that follow-up lookup, so the person can pick their actual unit
  // instead of either getting a fake value or having to know it blank.
  const [unitChoices, setUnitChoices] = React.useState([]);
  const [unitLoading, setUnitLoading] = React.useState(false);
  // 2026-10-09 — real bug, reported directly: picking a street suggestion
  // sets `line1` to Smarty's own street_line, which almost always differs
  // from what the person actually typed. That line1 change re-triggers
  // THIS effect ~250ms later, repopulating `suggestions` — an absolute,
  // zIndex:10 overlay that then paints on top of the unit-picker box
  // below it (which has no zIndex of its own), silently eating clicks on
  // the real unit chips. This ref lets `choose()` skip exactly one
  // re-fetch for the line1 change IT causes, without disabling the
  // lookup for the person's own subsequent typing.
  const skipNextSuggestLookup = React.useRef(false);

  React.useEffect(() => {
    if (skipNextSuggestLookup.current) { skipNextSuggestLookup.current = false; return undefined; }
    const term = (value.line1 || "").trim();
    if (!SMARTY_WEBSITE_KEY || term.length < 3) { setSuggestions([]); return undefined; }
    const timer = setTimeout(() => {
      const url = `https://us-autocomplete-pro.api.smarty.com/lookup?key=${encodeURIComponent(SMARTY_WEBSITE_KEY)}&search=${encodeURIComponent(term)}&max_results=5`;
      fetch(url).then((r) => (r.ok ? r.json() : { suggestions: [] }))
        .then((d) => setSuggestions(d.suggestions || []))
        .catch(() => setSuggestions([]));
    }, 250);
    return () => clearTimeout(timer);
  }, [value.line1]);

  const choose = (sg) => {
    const hasSpecificUnit = (sg.entries || 0) <= 1;
    skipNextSuggestLookup.current = true;
    onChange({ ...value, line1: sg.street_line || "", city: sg.city || "", state: sg.state || "", zip: sg.zipcode || "", unit: hasSpecificUnit ? (sg.secondary || "") : "" });
    setSuggestions([]);
    setUnitChoices([]);
    if (!hasSpecificUnit) {
      // Follow-up lookup: Smarty returns the real list of units at this
      // specific address when you pass the chosen suggestion back as
      // `selected`, instead of a fresh free-text `search`.
      setUnitLoading(true);
      // Smarty's documented `selected` format (confirmed against Smarty's
      // own US Autocomplete Pro docs, 2026-10-09): NOT a reconstructed,
      // comma-punctuated string — it's "street_line secondary (entries)
      // city state zipcode", e.g. "1042 W Center St Apt (108) Orem UT
      // 84057". The entries COUNT goes in parentheses right after
      // secondary; secondary is still included even when it's just the
      // generic placeholder (e.g. "Apt"). Our old comma-joined string
      // that dropped secondary entirely is what produced Smarty's 422 —
      // a malformed-request response, not "no results."
      const selected = [sg.street_line, sg.secondary, sg.entries ? `(${sg.entries})` : null, sg.city, sg.state, sg.zipcode]
        .filter(Boolean).join(" ");
      const url = `https://us-autocomplete-pro.api.smarty.com/lookup?key=${encodeURIComponent(SMARTY_WEBSITE_KEY)}&search=${encodeURIComponent(sg.street_line || "")}&selected=${encodeURIComponent(selected)}&max_results=10`;
      fetch(url).then((r) => (r.ok ? r.json() : { suggestions: [] }))
        .then((d) => setUnitChoices(d.suggestions || []))
        .catch(() => setUnitChoices([]))
        .finally(() => setUnitLoading(false));
    }
  };

  const chooseUnit = (sg) => {
    onChange({ ...value, unit: sg.secondary || "" });
    setUnitChoices([]);
  };

  return (
    <Box sx={{ position: "relative", mb: 1 }}>
      <TextField hiddenLabel fullWidth placeholder="Street address" sx={inputSx}
        error={!!error} helperText={error ? "Required" : ""}
        value={value.line1} onChange={(e) => onChange({ ...value, line1: e.target.value })} />
      {suggestions.length > 0 && (
        <Box sx={{ position: "absolute", zIndex: 10, left: 0, right: 0, mt: 0.5, bgcolor: isDark ? "#1e293b" : "#fff", border: "1px solid #cbd5e1", borderRadius: 1 }}>
          {suggestions.map((sg, i) => (
            <Box key={i} onMouseDown={(e) => { e.preventDefault(); choose(sg); }}
              sx={{ px: 1.5, py: 1, cursor: "pointer", fontSize: 14, "&:hover": { bgcolor: "rgba(0,0,0,0.06)" } }}>
              {sg.street_line}{(sg.entries || 0) > 1 ? " (multiple units)" : (sg.secondary ? ` ${sg.secondary}` : "")}, {sg.city}, {sg.state} {sg.zipcode}
            </Box>
          ))}
        </Box>
      )}
      {(unitLoading || unitChoices.length > 0) && (
        <Box sx={{ mt: 0.5, p: 1, border: "1px solid #cbd5e1", borderRadius: 1, bgcolor: isDark ? "#1e293b" : "#f8fafc" }}>
          <Typography sx={{ fontSize: 12.5, color: "text.secondary", mb: 0.5 }}>
            This building has multiple units — select yours:
          </Typography>
          {unitLoading ? (
            <Typography sx={{ fontSize: 13, color: "text.secondary" }}>Loading units…</Typography>
          ) : (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {unitChoices.map((sg, i) => (
                <Box key={i} onMouseDown={(e) => { e.preventDefault(); chooseUnit(sg); }}
                  sx={{ px: 1.25, py: 0.5, cursor: "pointer", fontSize: 13.5, border: "1px solid #cbd5e1", borderRadius: 1, "&:hover": { bgcolor: "rgba(0,0,0,0.06)" } }}>
                  {sg.secondary || "(no unit number)"}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

// 2026-10-15 fix (proper mandatory validation + tailored input fields,
// explicit user request: "address is mandatory everywhere") — `showErrors`
// makes every field actually show a red border + helper text when blank
// or malformed; `freeSolo` on the state Autocomplete used to let someone
// type/submit arbitrary text (e.g. "Calfornia") despite the dropdown
// looking like an enforced 2-letter list — removed so only a real state
// code can be selected; zip now has real 5(+4)-digit format validation.
export function AddressFields({ isDark, inputSx, value, onChange, showErrors = false }) {
  const line1Error = showErrors && !value.line1?.trim();
  const cityError = showErrors && !value.city?.trim();
  const stateError = showErrors && !US_STATES.includes(value.state);
  const zipError = showErrors && !isValidZip(value.zip);
  return (
    <>
      <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 1 }}>
        Enter the patient's current address — used to correctly match this test to the right person.
      </Typography>
      <StreetAutocomplete isDark={isDark} inputSx={inputSx} value={value} onChange={onChange} error={line1Error} />
      <TextField hiddenLabel placeholder="Apt / Suite / Unit (optional)" fullWidth sx={{ ...inputSx, mb: 1 }}
        value={value.unit || ""} onChange={(e) => onChange({ ...value, unit: e.target.value })} />
      <Box sx={{ display: "flex", gap: 1 }}>
        <TextField hiddenLabel placeholder="City" sx={{ ...inputSx, flex: 2 }}
          error={cityError} helperText={cityError ? "Required" : ""}
          value={value.city} onChange={(e) => onChange({ ...value, city: e.target.value })} />
        <Autocomplete
          options={US_STATES}
          value={value.state || null}
          onChange={(e, newValue) => onChange({ ...value, state: newValue || "" })}
          autoHighlight
          sx={{ flex: 1 }}
          renderInput={(params) => (
            <TextField {...params} hiddenLabel placeholder="State" sx={inputSx}
              error={stateError} helperText={stateError ? "Required" : ""} />
          )}
        />
        <TextField hiddenLabel placeholder="ZIP" sx={{ ...inputSx, flex: 1 }}
          error={zipError} helperText={zipError ? "5 digits" : ""}
          inputProps={{ maxLength: 10, inputMode: "numeric" }}
          value={value.zip} onChange={(e) => onChange({ ...value, zip: e.target.value.replace(/[^\d-]/g, "") })} />
      </Box>
    </>
  );
}
